// src/store/vcardStore.ts
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */

import { create } from "zustand";
import {
  collection,
  updateDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useDebugStore } from "./debugStore";
import { UniversalDirectoryManager } from "../services/storage/universalDirectoryManager";
import type { VCard, VCardCategory } from "../types/vcard";

const SNOW_GEAR_SUBCATEGORIES = [
  "snowboards",
  "helmetMask",
  "boots",
  "glovesApparel",
] as const;
type SnowGearSubcategoryKey = (typeof SNOW_GEAR_SUBCATEGORIES)[number];

const getDefaultSnowGearSubsections = (): Record<
  SnowGearSubcategoryKey,
  boolean
> => ({
  snowboards: false,
  helmetMask: false,
  boots: false,
  glovesApparel: false,
});

const deriveSnowGearSubsections = (vcards: VCard[]) => {
  const subsections = getDefaultSnowGearSubsections();

  vcards.forEach((card) => {
    if (!card?.category || !card.category.startsWith("snowGear_")) return;
    const key = card.category.replace(
      "snowGear_",
      "",
    ) as SnowGearSubcategoryKey;
    if (key in subsections) {
      subsections[key] = !!card.isActive;
    }
  });

  return subsections;
};

const areSnowGearSubsectionsEqual = (
  a: Partial<Record<SnowGearSubcategoryKey, boolean>> | undefined,
  b: Partial<Record<SnowGearSubcategoryKey, boolean>> | undefined,
) => SNOW_GEAR_SUBCATEGORIES.every((key) => !!a?.[key] === !!b?.[key]);

const stripUndefined = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(stripUndefined).filter((item) => item !== undefined);
  }

  if (value && typeof value === "object") {
    const result: Record<string, unknown> = {};
    Object.entries(value).forEach(([key, val]) => {
      if (val === undefined) return;
      const cleaned = stripUndefined(val);
      if (cleaned !== undefined) {
        result[key] = cleaned;
      }
    });
    return result;
  }

  return value;
};

const isAddressComplete = (
  address?: VCard["address"] | null,
): address is VCard["address"] => {
  if (!address) return false;
  const required = [
    address.street,
    address.city,
    address.state,
    address.zip,
    address.country,
  ];
  return required.every(
    (value) => typeof value === "string" && value.trim().length > 0,
  );
};

const buildAddressFromHotel = (
  hotelAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  } | null,
): VCard["address"] | null => {
  if (!hotelAddress) return null;
  const address = {
    street: hotelAddress.line1?.trim() || "",
    unit: hotelAddress.line2?.trim() || "",
    city: hotelAddress.city?.trim() || "",
    state: hotelAddress.state?.trim() || "",
    zip: hotelAddress.zip?.trim() || "",
    country: hotelAddress.country?.trim() || "",
  };

  return isAddressComplete(address) ? address : null;
};

interface VCardStore {
  vcards: VCard[];
  loading: boolean;
  error: string | null;
  toggleVCardWithAddress: (
    vcardIdOrCategory: string,
    userId: string,
  ) => Promise<void>;
  fetchUserVCards: (userId: string) => Promise<void>;
  createVCard: (vcard: VCard) => Promise<void>;
  updateVCard: (id: string, updatedData: Partial<VCard>) => Promise<void>;
}

export const useVCardStore = create<VCardStore>((set, get) => ({
  vcards: [],
  loading: false,
  error: null,

  toggleVCardWithAddress: async (vcardIdOrCategory: string, userId: string) => {
    const addDebugMessage = useDebugStore.getState().addMessage;
    set({ loading: true, error: null });

    try {
      let vcard = get().vcards.find(
        (v) => v.id === vcardIdOrCategory && v.userId === userId,
      );

      if (!vcard) {
        if (vcardIdOrCategory.startsWith("snowGear_")) {
          vcard = get().vcards.find(
            (v) =>
              v.category === (vcardIdOrCategory as VCardCategory) &&
              v.userId === userId,
          );
        } else {
          vcard = get().vcards.find(
            (v) =>
              v.category === (vcardIdOrCategory as VCardCategory) &&
              v.userId === userId,
          );
        }
      }

      if (!vcard) {
        throw new Error(`No vCard found for ${vcardIdOrCategory}`);
      }

      const category = vcard.category;
      addDebugMessage("info", `Toggling ${category} vCard with address swap`);

      const newState = !vcard.isActive;
      const luggageVCard = get().vcards.find(
        (v) => v.category === "luggage" && v.userId === userId,
      );
      const homeVCard = get().vcards.find(
        (v) => v.category === "home" && v.userId === userId,
      );

      const completeHomeAddress = isAddressComplete(homeVCard?.address)
        ? homeVCard.address
        : null;

      const newAddress =
        category === "luggage" && newState
          ? (buildAddressFromHotel(luggageVCard?.hotelInfo?.address) ??
            completeHomeAddress)
          : completeHomeAddress;

      const updatePayload: Partial<VCard> = { isActive: newState };
      if (newAddress) {
        updatePayload.address = newAddress;
      }

      const path = UniversalDirectoryManager.getPath({
        userId,
        contentType: "vcard",
      });

      const vcardRef = doc(db, path, vcard.id);
      await updateDoc(vcardRef, {
        ...stripUndefined(updatePayload),
        updatedAt: serverTimestamp(),
      });

      let nextVCards = get().vcards.map((v) =>
        v.id === vcard.id ? { ...v, ...updatePayload } : v,
      );

      if (category === "snowGear" || category.startsWith("snowGear_")) {
        const parent = nextVCards.find(
          (v) => v.category === "snowGear" && v.userId === userId,
        );
        if (parent) {
          let subsections =
            parent.categorySpecificInfo?.snowGear?.status?.subsections ||
            getDefaultSnowGearSubsections();

          if (category === "snowGear") {
            subsections = SNOW_GEAR_SUBCATEGORIES.reduce(
              (acc, key) => {
                acc[key] = newState;
                return acc;
              },
              {} as Record<SnowGearSubcategoryKey, boolean>,
            );
          } else {
            const childKey = category.replace(
              "snowGear_",
              "",
            ) as SnowGearSubcategoryKey;
            if (childKey in subsections) {
              subsections = { ...subsections, [childKey]: newState };
            }
          }

          const anyActive = Object.values(subsections).some(Boolean);
          const updatedParent: Partial<VCard> = {
            isActive: anyActive,
            categorySpecificInfo: {
              ...parent.categorySpecificInfo,
              snowGear: {
                ...parent.categorySpecificInfo?.snowGear,
                status: {
                  ...(parent.categorySpecificInfo?.snowGear?.status || {}),
                  isActive: anyActive,
                  subsections,
                },
              },
            },
          };

          const parentRef = doc(db, path, parent.id);
          await updateDoc(parentRef, {
            ...stripUndefined(updatedParent),
            updatedAt: serverTimestamp(),
          });

          nextVCards = nextVCards.map((v) =>
            v.id === parent.id ? { ...v, ...updatedParent } : v,
          );
        } else {
          addDebugMessage(
            "warning",
            "snowGear parent vCard missing; cannot sync subsections",
          );
        }
      }

      set({ vcards: nextVCards, loading: false });
      addDebugMessage(
        "success",
        `Successfully toggled ${category} vCard with address swap`,
      );
    } catch (error: any) {
      set({ error: error.message, loading: false });
      addDebugMessage(
        "error",
        `Error toggling vCard with address: ${error.message}`,
      );
      throw error;
    }
  },

  fetchUserVCards: async (userId: string) => {
    const addDebugMessage = useDebugStore.getState().addMessage;
    set({ loading: true, error: null });

    try {
      // First verify the user's storage structure
      await UniversalDirectoryManager.verifyUserStructure(userId);

      // Fetch from the active path only, as repository vCards are no longer used
      const activePath = UniversalDirectoryManager.getPath({
        userId,
        contentType: "vcard",
        state: "active",
      });

      // Get active VCards
      const activeVCardsRef = collection(db, activePath);
      const activeSnapshot = await getDocs(query(activeVCardsRef));

      // Map the documents to VCard objects using the actual stored isActive value
      const activeVCards = activeSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as VCard[];

      addDebugMessage("info", `Found ${activeVCards.length} active VCards`);

      // Reconcile snowGear parent status with child vCards to prevent drift
      let nextVCards = activeVCards;
      const snowGearParent = nextVCards.find(
        (v) => v.category === "snowGear" && v.userId === userId,
      );
      if (snowGearParent) {
        const derivedSubsections = deriveSnowGearSubsections(nextVCards);
        const anyActive = Object.values(derivedSubsections).some(Boolean);
        const currentSubsections =
          snowGearParent.categorySpecificInfo?.snowGear?.status?.subsections;

        if (
          !areSnowGearSubsectionsEqual(
            currentSubsections,
            derivedSubsections,
          ) ||
          snowGearParent.isActive !== anyActive
        ) {
          const updatedParent: Partial<VCard> = {
            isActive: anyActive,
            categorySpecificInfo: {
              ...snowGearParent.categorySpecificInfo,
              snowGear: {
                ...snowGearParent.categorySpecificInfo?.snowGear,
                status: {
                  ...(snowGearParent.categorySpecificInfo?.snowGear?.status ||
                    {}),
                  isActive: anyActive,
                  subsections: derivedSubsections,
                },
              },
            },
          };

          const parentRef = doc(db, activePath, snowGearParent.id);
          await updateDoc(parentRef, {
            ...stripUndefined(updatedParent),
            updatedAt: serverTimestamp(),
          });

          nextVCards = nextVCards.map((v) =>
            v.id === snowGearParent.id ? { ...v, ...updatedParent } : v,
          );

          addDebugMessage(
            "info",
            "Reconciled snowGear parent status with child vCards",
          );
        }
      }

      // Update the store with the vCards (post-reconciliation)
      set({ vcards: nextVCards, loading: false });
      addDebugMessage(
        "success",
        `Loaded ${activeVCards.length} active VCards successfully`,
      );
    } catch (error: any) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      set({ error: errorMessage, loading: false });
      addDebugMessage("error", `Failed to fetch VCards: ${errorMessage}`);
      throw error;
    }
  },

  createVCard: async (vcard: VCard) => {
    const addDebugMessage = useDebugStore.getState().addMessage;
    set({ loading: true, error: null });

    try {
      // Get the base path for vcards
      const basePath = UniversalDirectoryManager.getPath({
        userId: vcard.userId,
        contentType: "vcard",
        state: "active",
      });

      addDebugMessage("info", `Creating vCard at path: ${basePath}`);
      addDebugMessage("info", `VCard data:`, vcard);

      // Create a document in the vcards collection with the category as the document ID
      const vcardRef = doc(db, basePath, vcard.category);
      await setDoc(vcardRef, {
        ...vcard,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newVCard = {
        ...vcard,
        id: vcard.category,
        isActive: true,
      };

      set((state) => ({
        vcards: [...state.vcards, newVCard],
        loading: false,
      }));

      addDebugMessage("info", `Created new ${vcard.category} vCard`);
    } catch (error: any) {
      set({ error: error.message, loading: false });
      addDebugMessage("error", `Error creating vCard: ${error.message}`, error);
    }
  },

  updateVCard: async (id: string, updatedData: Partial<VCard>) => {
    const addDebugMessage = useDebugStore.getState().addMessage;
    set({ loading: true, error: null });

    try {
      const vcard = get().vcards.find((v) => v.id === id);
      if (!vcard) {
        throw new Error("VCard not found");
      }

      // Get the path based on the current state
      const basePath = UniversalDirectoryManager.getPath({
        userId: vcard.userId,
        contentType: "vcard",
        state: vcard.isActive ? "active" : "repository",
      });

      addDebugMessage("info", `Updating vCard at path: ${basePath}`);
      addDebugMessage("info", `Update data:`, updatedData);

      // Update the document
      const vcardRef = doc(db, basePath, id);
      const sanitizedData = stripUndefined(updatedData);
      await updateDoc(vcardRef, {
        ...sanitizedData,
        updatedAt: serverTimestamp(),
      });

      // Update local store
      set((state) => ({
        vcards: state.vcards.map((v) =>
          v.id === id ? { ...v, ...updatedData } : v,
        ),
        loading: false,
      }));

      addDebugMessage("info", `Updated ${vcard.category} vCard`);
    } catch (error: any) {
      set({ error: error.message, loading: false });
      addDebugMessage("error", `Error updating vCard: ${error.message}`, error);
      throw error;
    }
  },
}));
